import React, { useState } from 'react'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { Alert, Box, Checkbox, FormControlLabel, Typography } from '@mui/material'
import DefaultModalActions from '../../../../components/GenericModal/DefaultModalActions'
import { ButtonType } from '../../../../components/GenericButton/styles'
import { type ReportActionKind, REPORT_ACTION_COPY, formatReportScope } from '../reportActionCopy'

interface ReportActionModalProps {
  // Which report action this confirms — drives all copy (see reportActionCopy).
  action: ReportActionKind
  // Display name of the Academic Unit / Course Section whose report is affected.
  targetName?: string
  // Survey project + term the action targets — named in the copy so the user is never
  // confused about which report is affected (especially from history/compare views).
  surveyProjectTitle?: string
  termName?: string
  // True when the report target is a Course Section (hides the include-children option,
  // shows the parent-rollup caveat).
  isSection?: boolean
  handleCloseModal?: () => void
  onConfirm: (includeChildren: boolean) => void
  isSubmitting?: boolean
}

// Confirmation body shared by the regenerate / revert / restore actions. The wording differs
// only by `action` (via REPORT_ACTION_COPY); the layout, include-children option, and
// parent-rollup caveat are identical across all three.
export const ReportActionModal = ({
  action,
  targetName,
  surveyProjectTitle,
  termName,
  isSection = false,
  handleCloseModal,
  onConfirm,
  isSubmitting = false
}: ReportActionModalProps): JSX.Element => {
  const [includeChildren, setIncludeChildren] = useState(false)
  const subject = isSection ? 'course section' : 'Academic Unit'
  const scope = formatReportScope(surveyProjectTitle, termName)
  const copy = REPORT_ACTION_COPY[action]

  return (
    <Box>
      <Alert severity='info' icon={<InfoOutlinedIcon />} sx={{ mb: 3 }}>
        <Typography fontSize='0.875rem'>
          This {copy.leadVerbPhrase} the cached report for{' '}
          <strong>{targetName ?? `this ${subject}`}</strong>{' '}
          {scope ? <>in <strong>{scope}</strong></> : 'in this survey project'}. It runs in the
          background and may take a few minutes to appear.
        </Typography>
      </Alert>

      {isSection
        ? (
          <Typography variant='body2' color='text.secondary' sx={{ mb: 3 }}>
            Note: {copy.gerund} a Course Section does not refresh its parent Academic Unit&apos;s
            rollup — {copy.verb} the Academic Unit (optionally with its children) to update that.
          </Typography>
          )
        : (
          <FormControlLabel
            sx={{ alignItems: 'flex-start', mb: 3, ml: 0 }}
            control={
              <Checkbox
                checked={includeChildren}
                onChange={(e) => { setIncludeChildren(e.target.checked) }}
                sx={{ pt: 0, pl: 0, mr: 1 }}
              />
            }
            label={
              <Box>
                <Typography variant='body2'>
                  Also {copy.verb} all child Academic Units and Course Sections
                </Typography>
                <Typography variant='caption' color='text.secondary'>
                  (slower; {copy.childrenCaptionVerb} everything underneath this Academic Unit)
                </Typography>
              </Box>
            }
          />
          )}

      <DefaultModalActions
        destructiveButtonProps={{ onClick: handleCloseModal }}
        confirmButtonProps={{
          onClick: () => { onConfirm(isSection ? false : includeChildren) },
          text: copy.confirmText,
          buttonType: ButtonType.Primary,
          loading: isSubmitting
        }}
      />
    </Box>
  )
}
